import { useEffect, useRef, useState } from 'react';

interface TokenCard {
  icon: string;
  number: string;
  label: string;
  description: string;
}

export default function TokenomicsSection() {
  const [isVisible, setIsVisible] = useState(false);
  const [counts, setCounts] = useState<Record<number, number>>({});
  const sectionRef = useRef<HTMLDivElement>(null);

  const cards: TokenCard[] = [
    {
      icon: 'ᚱ',
      number: '1B',
      label: 'TOTAL SUPPLY',
      description: 'Fixed supply of 1,000,000,000 VALL tokens'
    },
    {
      icon: 'ᚢ',
      number: '10%',
      label: 'CREATOR FEES',
      description: 'Goes directly to buy back the token'
    },
    {
      icon: 'ᚾ',
      number: '100%',
      label: 'LP BURNED',
      description: 'Liquidity locked forever in the flames'
    },
    {
      icon: 'ᛖ',
      number: '0%',
      label: 'TAX',
      description: 'Fair launch with no hidden fees'
    }
  ];

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.3 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (isVisible) {
      cards.forEach((_, index) => {
        let start = 0;
        const duration = 2000;
        const startTime = Date.now();

        const animate = () => {
          const now = Date.now();
          const progress = Math.min((now - startTime) / duration, 1);
          const current = Math.floor(progress * 100);
          
          setCounts(prev => ({ ...prev, [index]: current }));

          if (progress < 1) {
            requestAnimationFrame(animate);
          }
        };

        setTimeout(() => animate(), index * 200);
      });
    }
  }, [isVisible]);

  return (
    <section ref={sectionRef} className="relative py-32 bg-[#1a1a1a] overflow-hidden">
      {/* Stone texture background */}
      <div className="absolute inset-0 opacity-10 bg-[url('https://public.readdy.ai/ai/img_res/ea67fc17db38d4b3b83c4ec597a08ffc.jpg')] bg-cover" />

      {/* Top gold border */}
      <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-[#d4af37] to-transparent" />

      {/* Floating gold particles */}
      {Array.from({ length: 30 }).map((_, i) => (
        <div
          key={i}
          className="absolute w-1 h-1 bg-[#d4af37] rounded-full opacity-40 animate-float-particle"
          style={{
            left: `${Math.random() * 100}%`,
            top: `${Math.random() * 100}%`,
            animationDelay: `${Math.random() * 5}s`,
            animationDuration: `${5 + Math.random() * 5}s`
          }}
        />
      ))}

      <div className="relative z-10 max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-20">
          <h2 
            className="text-6xl lg:text-7xl font-bold text-white mb-4 tracking-[0.2em] uppercase"
            style={{ 
              WebkitTextStroke: '2px #d4af37',
              fontFamily: 'Cinzel, serif'
            }}
          >
            TOKENOMICS
          </h2>
          <p className="text-[#d4af37] text-xl italic" style={{ fontFamily: 'Cinzel, serif' }}>
            The Distribution of Power
          </p>
        </div>

        {/* Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
          {cards.map((card, index) => (
            <div
              key={index}
              className={`group relative bg-black/60 border-2 border-[#d4af37] rounded-3xl p-12 transition-all duration-500 hover:shadow-[0_0_30px_rgba(212,175,55,0.8)] hover:-translate-y-2 cursor-pointer ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              }`}
              style={{ transitionDelay: `${index * 150}ms` }}
            >
              {/* Icon area */}
              <div className="flex justify-center mb-8">
                <div className="w-20 h-20 rounded-full border-3 border-[#d4af37] flex items-center justify-center bg-[radial-gradient(circle_at_center,rgba(212,175,55,0.3)_0%,transparent_70%)]">
                  <span className="text-white text-4xl">{card.icon}</span>
                </div>
              </div>

              {/* Number */}
              <div className="text-center mb-3">
                <h3 className="text-7xl font-bold text-white">
                  {card.number}
                </h3>
              </div>

              {/* Label */}
              <div className="text-center mb-5">
                <p className="text-[#d4af37] text-base uppercase tracking-[0.2em] font-semibold">
                  {card.label}
                </p>
              </div>

              {/* Description */}
              <div className="text-center">
                <p className="text-[#b8b8b8] text-sm leading-relaxed">
                  {card.description}
                </p>
              </div>

              {/* Hover glow effect */}
              <div className="absolute inset-0 rounded-3xl bg-[#d4af37] opacity-0 group-hover:opacity-10 transition-opacity duration-500 pointer-events-none" />
            </div>
          ))}
        </div>

        {/* Bottom rune decoration */}
        <div className="flex justify-center gap-8 mt-16 text-[#d4af37] text-3xl opacity-50">
          <span>ᚱ</span>
          <span>ᚢ</span>
          <span>ᚾ</span>
          <span>ᛖ</span>
        </div>
      </div>
    </section>
  );
}
